/*
 * The live camera's adjustments, shared by every camera shown inside the app
 * (Field Mode's, the dish station's, the microscope camera, the QR scanner):
 * what the camera itself lets a page change, the constraints that change it,
 * and a DIGITAL brightness where it lets nothing change. Pure, no React.
 *
 * What a camera offers is read from MediaStreamTrack.getCapabilities() and
 * getSettings() (readCapabilities), never assumed: Chrome on most Android
 * phones offers exposure compensation, a focus mode and distance, a point to
 * focus on and a torch; an iPhone's Safari and most laptops offer none of
 * them. The page shows only what is offered.
 *
 *   exposure   'camera': exposure compensation in EV, set on the camera
 *              (exposureConstraints), so the picture itself is brighter or
 *              darker. 'digital' where the camera has none: the same slider
 *              in stops of DIGITAL_RANGE, applied to the preview as a CSS
 *              brightness and to the captured picture the same way
 *              (utils/fieldFrame.grabFrame's `gain`), and labelled digital.
 *   focus      the camera's own automatic focus, or -- where it offers a
 *              manual mode and a distance -- a distance slider
 *              (focusConstraints); where it offers a point of interest, a tap
 *              on the picture focuses and exposes there (pointConstraints).
 *   torch      the phone's light, where it has one.
 *
 * The Pro drawer's controls (2026-10-22; docs/FIELD_AI_VISION_SPEC.md 10.1),
 * each only where the camera offers it:
 *
 *   focus lock     manual focus at the distance the automatic focus found
 *                  (focusLock 'manual'), else a 'single-shot' focus that
 *                  holds (focusLock 'single-shot').
 *   exposure lock  exposureMode 'manual' at the exposure time and ISO the
 *                  automatic exposure had (lockedExposureConstraints); then
 *                  the shutter (exposureTime, in the Image Capture spec's
 *                  100-microsecond units) and ISO are set by hand.
 *   white balance  whiteBalanceMode 'manual' at a colour temperature (K).
 *   zoom           the camera's own zoom: never a digital one, which would
 *                  only crop. A pinch on the picture sets it too.
 *   aperture       no browser reports or sets one: always 'fixed'.
 */

//: the digital brightness slider, in stops (x0.35 to x2.8)
export const DIGITAL_RANGE = Object.freeze({ min: -1.5, max: 1.5, step: 0.1 });

function call(track, name) {
  if (!track || typeof track[name] !== 'function') return {};
  try {
    return track[name]() || {};
  } catch {
    return {};
  }
}

function range(r) {
  if (!r || !Number.isFinite(r.min) || !Number.isFinite(r.max) || !(r.max > r.min)) return null;
  return { min: r.min, max: r.max, step: r.step > 0 ? r.step : (r.max - r.min) / 100 };
}

const offers = (value, wanted) => value === wanted || (Array.isArray(value) && value.includes(wanted));

/** A range with the value the camera has now, else `fallback` (default: its minimum). */
function ranged(r, now, fallback) {
  const span = range(r);
  if (!span) return null;
  const value = Number.isFinite(now) ? now : (Number.isFinite(fallback) ? fallback : span.min);
  return { ...span, value: clamp(value, span) };
}

/** `value` inside {min, max}. */
export function clamp(value, { min, max }) {
  return Math.min(max, Math.max(min, value));
}

/** What the camera reports now: its live settings, {} where it says nothing. */
export const readSettings = track => call(track, 'getSettings');

/**
 * What this camera lets the page change. {exposure: {kind: 'camera'|'digital',
 * min, max, step, value}, autoFocusMode, manualFocus, focusDistance: {min,
 * max, step, value}|null, pointOfInterest, torch, exposureMode, and the Pro
 * drawer's: focusLock 'manual'|'single-shot'|null, exposureLock,
 * exposureTime and iso {min, max, step, value}|null, whiteBalance {autoMode,
 * temperature: {min, max, step, value}}|null, zoom {min, max, step, value}|null,
 * aperture 'fixed'}.
 */
export function readCapabilities(track) {
  const caps = call(track, 'getCapabilities');
  const settings = call(track, 'getSettings');
  const ev = range(caps.exposureCompensation);
  const focusModes = Array.isArray(caps.focusMode) ? caps.focusMode : [];
  const distance = range(caps.focusDistance);
  const autoFocusMode = focusModes.includes('continuous') ? 'continuous'
    : focusModes.includes('single-shot') ? 'single-shot' : null;
  const manualFocus = !!distance && focusModes.includes('manual');
  const exposureTime = ranged(caps.exposureTime, settings.exposureTime);
  const iso = ranged(caps.iso, settings.iso);
  const temperature = ranged(caps.colorTemperature, settings.colorTemperature,
    range(caps.colorTemperature) ? (caps.colorTemperature.min + caps.colorTemperature.max) / 2 : null);
  const wbAuto = offers(caps.whiteBalanceMode, 'continuous') ? 'continuous'
    : offers(caps.whiteBalanceMode, 'single-shot') ? 'single-shot' : null;
  return {
    exposure: ev
      ? { kind: 'camera', ...ev, value: Number.isFinite(settings.exposureCompensation) ? settings.exposureCompensation : 0 }
      : { kind: 'digital', ...DIGITAL_RANGE, value: 0 },
    exposureMode: offers(caps.exposureMode, 'continuous') ? 'continuous' : null,
    autoFocusMode,
    manualFocus,
    focusDistance: distance
      ? { ...distance, value: Number.isFinite(settings.focusDistance) ? settings.focusDistance : distance.min }
      : null,
    pointOfInterest: 'pointsOfInterest' in caps || 'pointsOfInterest' in settings,
    torch: offers(caps.torch, true),
    // A single-shot focus is a lock only where the automatic focus is a continuous one.
    focusLock: manualFocus ? 'manual'
      : (focusModes.includes('single-shot') && focusModes.includes('continuous') ? 'single-shot' : null),
    exposureLock: offers(caps.exposureMode, 'manual') && !!exposureTime,
    exposureTime,
    iso,
    whiteBalance: offers(caps.whiteBalanceMode, 'manual') && temperature ? { autoMode: wbAuto, temperature } : null,
    zoom: ranged(caps.zoom, settings.zoom),
    aperture: 'fixed',
  };
}

/** The camera's exposure compensation set to `ev`, its exposure left automatic where it can be. */
export function exposureConstraints(ev, caps = {}) {
  return { advanced: [{ ...(caps.exposureMode ? { exposureMode: caps.exposureMode } : {}), exposureCompensation: ev }] };
}

/** Manual focus at `distance`, or the camera's own automatic focus back. */
export function focusConstraints({ manual, distance }, caps = {}) {
  if (manual) return { advanced: [{ focusMode: 'manual', focusDistance: distance }] };
  return { advanced: [{ focusMode: caps.autoFocusMode || 'continuous' }] };
}

/** Focus and expose at a point of the picture, x and y from 0 to 1 from its top-left: the point meters the
 *  light too, with the exposure left automatic where the camera can (2026-10-20). */
export function pointConstraints(x, y, caps = {}) {
  const point = { x: Math.max(0, Math.min(1, x)), y: Math.max(0, Math.min(1, y)) };
  return { advanced: [{ pointsOfInterest: [point], ...(caps.autoFocusMode ? { focusMode: caps.autoFocusMode } : {}),
                        ...(caps.exposureMode ? { exposureMode: caps.exposureMode } : {}) }] };
}

export const torchConstraints = on => ({ advanced: [{ torch: !!on }] });

/** The focus held where it is: manual at `distance` (the one the automatic focus found), else a single shot. */
export function focusLockConstraints(distance, caps = {}) {
  if (caps.focusLock === 'manual') return { advanced: [{ focusMode: 'manual', focusDistance: distance }] };
  return { advanced: [{ focusMode: 'single-shot' }] };
}

/** The exposure set by hand: a shutter time (100 µs units) and, where the camera has one, an ISO. */
export function lockedExposureConstraints({ exposureTime, iso }, caps = {}) {
  return { advanced: [{ exposureMode: 'manual', exposureTime,
                        ...(caps.iso && Number.isFinite(iso) ? { iso } : {}) }] };
}

/** White balance by hand at `temperature` K, or the camera's own back. */
export function whiteBalanceConstraints({ manual, temperature }, caps = {}) {
  if (manual) return { advanced: [{ whiteBalanceMode: 'manual', colorTemperature: temperature }] };
  return { advanced: [{ whiteBalanceMode: caps.whiteBalance?.autoMode || 'continuous' }] };
}

export const zoomConstraints = zoom => ({ advanced: [{ zoom }] });

/** The zoom a pinch asks for: where it began times how far the fingers spread, on the camera's steps. */
export function zoomForPinch(start, scale, zoom) {
  if (!zoom || !(scale > 0) || !Number.isFinite(start)) return null;
  const wanted = clamp(start * scale, zoom);
  const stepped = zoom.min + Math.round((wanted - zoom.min) / zoom.step) * zoom.step;
  return Math.round(clamp(stepped, zoom) * 1000) / 1000;
}

/**
 * A long range on a slider whose middle is not its middle -- a shutter from
 * 1/8000 s to 1/3 s, an ISO from 50 to 6400 -- as positions 0..SLIDER_STEPS,
 * evenly in stops. Pure, so the slider and its label agree.
 */
export const SLIDER_STEPS = 1000;
export function toLogPosition(value, { min, max }) {
  const lo = Math.max(min, 1e-6);
  if (!(max > lo)) return 0;
  return Math.round((Math.log(clamp(value, { min: lo, max }) / lo) / Math.log(max / lo)) * SLIDER_STEPS);
}
export function fromLogPosition(position, { min, max, step }) {
  const lo = Math.max(min, 1e-6);
  const value = lo * (max / lo) ** (clamp(position, { min: 0, max: SLIDER_STEPS }) / SLIDER_STEPS);
  const stepped = step > 0 ? min + Math.round((value - min) / step) * step : value;
  return Math.round(clamp(stepped, { min, max }) * 1000) / 1000;
}

/** An exposure time in 100 µs units as a photographer says it: "1/250 s", "0.5 s". */
export function shutterLabel(units) {
  if (!Number.isFinite(units) || units <= 0) return '–';
  const seconds = units / 10000;
  if (seconds >= 0.3) return `${Math.round(seconds * 10) / 10} s`;
  return `1/${Math.round(1 / seconds)} s`;
}

/** The digital brightness of `stops`: the factor applied to the preview and the picture (1: none). */
export const digitalGain = stops => (Number.isFinite(stops) && stops !== 0 ? Math.round(2 ** stops * 1000) / 1000 : 1);

/** RGBA pixels brightened by `gain`, in place: for a canvas that has no `filter`. */
export function brightenPixels(rgba, gain) {
  if (!(gain > 0) || gain === 1) return rgba;
  for (let i = 0; i < rgba.length; i += 4) {
    rgba[i] = Math.min(255, rgba[i] * gain);
    rgba[i + 1] = Math.min(255, rgba[i + 1] * gain);
    rgba[i + 2] = Math.min(255, rgba[i + 2] * gain);
  }
  return rgba;
}

/** Where on the picture (0-1 from its top-left) a tap landed, for a video shown with object-fit cover or contain. */
export function tapPoint(event, video, fit = 'cover') {
  const rect = video.getBoundingClientRect();
  const vw = video.videoWidth || rect.width;
  const vh = video.videoHeight || rect.height;
  if (!rect.width || !rect.height || !vw || !vh) return null;
  const scale = fit === 'contain' ? Math.min(rect.width / vw, rect.height / vh) : Math.max(rect.width / vw, rect.height / vh);
  const shownW = vw * scale;
  const shownH = vh * scale;
  const x = (event.clientX - rect.left - (rect.width - shownW) / 2) / shownW;
  const y = (event.clientY - rect.top - (rect.height - shownH) / 2) / shownH;
  if (x < 0 || y < 0 || x > 1 || y > 1) return null;
  return { x, y };
}
